import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_URL || '';

interface CrmClient {
  id: string;
  name: string;
  email: string;
  company?: string;
  tenantId: string;
  tenantName: string;
  forcePasswordChange: boolean;
}

interface LoginResponse {
  token: string;
  client: CrmClient;
  expiresAt: string;
}

interface CrmClientInfo {
  id: string;
  tenantId: string;
  tenantName: string;
  name: string;
  email: string;
  phone?: string;
  company?: string;
  abn?: string;
  status: string;
  intakeProgress: number;
  forcePasswordChange: boolean;
  lastLoginAt?: string;
  createdAt: string;
}

const TOKEN_KEY = 'crm_token';
const CLIENT_KEY = 'crm_client';

class CrmAuthService {
  private token: string | null = null;
  private client: CrmClient | null = null;

  constructor() {
    // Load from storage on init
    this.token = localStorage.getItem(TOKEN_KEY);
    const clientData = localStorage.getItem(CLIENT_KEY);
    if (clientData) {
      try {
        this.client = JSON.parse(clientData);
      } catch {
        this.client = null;
      }
    }
  }

  async signIn(email: string, password: string): Promise<{
    success: boolean;
    error?: string;
    requiresPasswordChange?: boolean;
    client?: CrmClient;
  }> {
    try {
      const response = await axios.post<LoginResponse>(`${API_BASE_URL}/crm/auth/login`, {
        email,
        password,
      });

      const { token, client, expiresAt } = response.data;

      // Store in memory and localStorage
      this.token = token;
      this.client = client;
      localStorage.setItem(TOKEN_KEY, token);
      localStorage.setItem(CLIENT_KEY, JSON.stringify(client));

      // Check if password change is required
      if (client.forcePasswordChange) {
        return {
          success: true,
          requiresPasswordChange: true,
          client,
        };
      }

      return {
        success: true,
        client,
      };
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || 'Login failed. Please check your credentials.';
      return {
        success: false,
        error: errorMessage,
      };
    }
  }

  async changePassword(currentPassword: string, newPassword: string): Promise<{
    success: boolean;
    error?: string;
  }> {
    try {
      await axios.post(
        `${API_BASE_URL}/crm/auth/change-password`,
        { currentPassword, newPassword },
        { headers: { Authorization: `Bearer ${this.token}` } }
      );

      // Update client in storage
      if (this.client) {
        this.client.forcePasswordChange = false;
        localStorage.setItem(CLIENT_KEY, JSON.stringify(this.client));
      }

      return { success: true };
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || 'Failed to change password';
      return { success: false, error: errorMessage };
    }
  }

  async signOut(): Promise<void> {
    try {
      if (this.token) {
        await axios.post(
          `${API_BASE_URL}/crm/auth/logout`,
          {},
          { headers: { Authorization: `Bearer ${this.token}` } }
        );
      }
    } catch {
      // Ignore errors on logout
    }

    // Clear storage
    this.token = null;
    this.client = null;
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(CLIENT_KEY);
  }

  async getCurrentClient(): Promise<CrmClientInfo | null> {
    if (!this.token) return null;

    try {
      const response = await axios.get<CrmClientInfo>(`${API_BASE_URL}/crm/auth/me`, {
        headers: { Authorization: `Bearer ${this.token}` },
      });
      return response.data;
    } catch {
      // Token may be expired
      this.signOut();
      return null;
    }
  }

  getToken(): string | null {
    return this.token;
  }

  getClient(): CrmClient | null {
    return this.client;
  }

  isAuthenticated(): boolean {
    return !!this.token;
  }

  requiresPasswordChange(): boolean {
    return this.client?.forcePasswordChange ?? false;
  }

  async forgotPassword(email: string): Promise<{
    success: boolean;
    message?: string;
    error?: string;
  }> {
    try {
      const response = await axios.post<{ success: boolean; message: string }>(
        `${API_BASE_URL}/crm/auth/forgot-password`,
        { email }
      );
      return {
        success: true,
        message: response.data.message,
      };
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || 'Failed to send password reset email';
      return { success: false, error: errorMessage };
    }
  }

  async resetPassword(token: string, email: string, newPassword: string): Promise<{
    success: boolean;
    message?: string;
    error?: string;
  }> {
    try {
      const response = await axios.post<{ success: boolean; message: string }>(
        `${API_BASE_URL}/crm/auth/reset-password`,
        { token, email, newPassword }
      );
      return {
        success: true,
        message: response.data.message,
      };
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || 'Failed to reset password';
      return { success: false, error: errorMessage };
    }
  }
}

export const crmAuth = new CrmAuthService();
